import { Matrix, Vector } from '@moleculeone/types';
import { mdot, mget } from './tools';
import { V4 } from './v4';

export class M4 {
  static flipX: Matrix = [-1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

  static flipY: Matrix = [1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

  static flipZ: Matrix = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 1];

  static id: Matrix = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

  static mulV(m: Matrix, v: Vector): Vector {
    // v[3] == 1 as in homogenous coordinates
    return [
      v[0] * mget(m, 0, 0) + v[1] * mget(m, 0, 1) + v[2] * mget(m, 0, 2) + mget(m, 0, 3),
      v[0] * mget(m, 1, 0) + v[1] * mget(m, 1, 1) + v[2] * mget(m, 1, 2) + mget(m, 1, 3),
      v[0] * mget(m, 2, 0) + v[1] * mget(m, 2, 1) + v[2] * mget(m, 2, 2) + mget(m, 2, 3),
    ];
  }

  static mulM(...matrices: Matrix[]): Matrix {
    if (matrices.length === 0) return M4.id;
    let m1 = matrices[0];
    for (let i = 1; i < arguments.length; i += 1) {
      const m2 = matrices[i];
      m1 = [
        mdot(m1, m2, 0, 0),
        mdot(m1, m2, 0, 1),
        mdot(m1, m2, 0, 2),
        mdot(m1, m2, 0, 3),
        mdot(m1, m2, 1, 0),
        mdot(m1, m2, 1, 1),
        mdot(m1, m2, 1, 2),
        mdot(m1, m2, 1, 3),
        mdot(m1, m2, 2, 0),
        mdot(m1, m2, 2, 1),
        mdot(m1, m2, 2, 2),
        mdot(m1, m2, 2, 3),
        mdot(m1, m2, 3, 0),
        mdot(m1, m2, 3, 1),
        mdot(m1, m2, 3, 2),
        mdot(m1, m2, 3, 3),
      ];
    }
    return m1;
  }

  static scaleS(s: number): Matrix {
    return [s, 0, 0, 0, 0, s, 0, 0, 0, 0, s, 0, 0, 0, 0, 1];
  }

  static scaleV(v: Vector): Matrix {
    return [v[0], 0, 0, 0, 0, v[1], 0, 0, 0, 0, v[2], 0, 0, 0, 0, 1];
  }

  static scaleVS(dir: Vector, scale: number): Matrix {
    const s = scale - 1;
    const v = V4.normal(dir);
    return [
      1 + v[0] * v[0] * s,
      v[0] * v[1] * s,
      v[0] * v[2] * s,
      0,
      v[1] * v[0] * s,
      1 + v[1] * v[1] * s,
      v[1] * v[2] * s,
      0,
      v[2] * v[0] * s,
      v[2] * v[1] * s,
      1 + v[2] * v[2] * s,
      0,
      0,
      0,
      0,
      1,
    ];
  }

  static flipV(v: Vector): Matrix {
    return M4.scaleVS(v, -1);
  }

  static rotZ(angle: number): Matrix {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return [cos, -sin, 0, 0, sin, cos, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  }

  static moveFrom(v: Vector): Matrix {
    return [1, 0, 0, -v[0], 0, 1, 0, -v[1], 0, 0, 1, -v[2], 0, 0, 0, 1];
  }

  static moveTo(v: Vector): Matrix {
    return [1, 0, 0, v[0], 0, 1, 0, v[1], 0, 0, 1, v[2], 0, 0, 0, 1];
  }

  static map(matrix: Matrix, positions: Record<string, Vector>): Record<string, Vector> {
    const result: Record<string, Vector> = {};
    for (const [idx, p] of Object.entries(positions)) {
      result[idx] = M4.mulV(matrix, p);
    }
    return result;
  }
}
